/**
 * Minimal two-language dictionary for the captive portal.
 *
 * Kept dependency free on purpose: the portal is served to users who are not
 * online yet, so every extra kilobyte is paid for over a captive connection.
 */

export const LOCALES = ["th", "en"];

const messages = {
  th: {
    tagline1: "ทำให้การซักผ้า",
    tagline2: "เป็นเรื่องง่ายในทุกชุมชน",
    phonePlaceholder: "กรอกเบอร์โทรศัพท์",
    phoneHint: "กรุณาใส่เบอร์โทรศัพท์เพื่อเริ่มใช้งาน",
    phoneInvalid: "เบอร์โทรศัพท์ไม่ถูกต้อง",
    confirm: "ยืนยัน",

    notMemberTitle: "ยังไม่มีบัญชี WashXpress",
    notMemberBody: "เบอร์ {phone} ยังไม่ได้สมัครใช้งานแอป WashXpress",
    notMemberAction: "กรุณาสมัครสมาชิกในแอปให้เรียบร้อยก่อน แล้วกลับมาเชื่อมต่อ WiFi อีกครั้ง",
    notMemberHint:
      "ดาวน์โหลดแอป WashXpress ได้ที่ App Store หรือ Google Play (ต้องใช้เน็ตมือถือในการดาวน์โหลด)",
    useAnotherNumber: "ใช้เบอร์อื่น",

    otpTitle: "ยืนยันรหัส OTP",
    otpSentTo: "ส่งรหัสยืนยันไปที่",
    otpRefCode: "รหัสอ้างอิง",
    otpPlaceholder: "กรอกรหัส 6 หลัก",
    otpHint: "กรุณากรอกรหัส OTP ที่ได้รับทาง SMS",
    otpInvalid: "กรุณากรอกรหัส OTP ให้ครบ 6 หลัก",
    resend: "ขอรหัสใหม่",
    resendIn: "ขอรหัสใหม่ได้ใน {seconds} วินาที",
    changeNumber: "เปลี่ยนเบอร์โทรศัพท์",

    connecting: "กำลังเชื่อมต่อ WiFi...",
    sending: "กำลังส่งรหัส...",
    verifying: "กำลังตรวจสอบ...",

    statusTitle: "เชื่อมต่ออินเทอร์เน็ตเรียบร้อยแล้ว",
    statusSubtitle: "ใช้งานอินเทอร์เน็ตได้เลย",
    statusAccount: "บัญชีผู้ใช้",
    statusIp: "IP ที่ได้รับ",
    statusMac: "อุปกรณ์ (MAC)",
    statusBack: "กลับไปหน้าหลัก",
    statusChecking: "กำลังตรวจสอบการเชื่อมต่อ...",
    statusFacebook: "ไปที่เพจ WashXpress",
    statusNoSession: "ไม่พบรายละเอียดการเชื่อมต่อ แต่หากเปิดเว็บได้แล้วแสดงว่าใช้งานได้ปกติ",

    errNetwork: "เชื่อมต่อระบบไม่ได้ กรุณาลองใหม่อีกครั้ง",
    errGeneric: "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง",
    errNoHotspot:
      "ไม่พบข้อมูลการเชื่อมต่อจากเครื่อง WiFi กรุณาเปิดหน้านี้ผ่านการเชื่อมต่อ WiFi อีกครั้ง",
  },
  en: {
    tagline1: "Laundry made simple",
    tagline2: "for every community",
    phonePlaceholder: "Enter your mobile number",
    phoneHint: "Enter your mobile number to get started",
    phoneInvalid: "Invalid mobile number",
    confirm: "Continue",

    notMemberTitle: "No WashXpress account",
    notMemberBody: "{phone} is not registered in the WashXpress app.",
    notMemberAction:
      "Please finish signing up in the app first, then come back to connect to the WiFi.",
    notMemberHint:
      "Get the WashXpress app from the App Store or Google Play (you will need mobile data to download it).",
    useAnotherNumber: "Use another number",

    otpTitle: "Verify OTP",
    otpSentTo: "We sent a code to",
    otpRefCode: "Ref code",
    otpPlaceholder: "Enter the 6-digit code",
    otpHint: "Enter the OTP code you received by SMS",
    otpInvalid: "Please enter all 6 digits",
    resend: "Resend code",
    resendIn: "Resend available in {seconds}s",
    changeNumber: "Change mobile number",

    connecting: "Connecting to WiFi...",
    sending: "Sending code...",
    verifying: "Verifying...",

    statusTitle: "You're connected",
    statusSubtitle: "You can start browsing now",
    statusAccount: "Account",
    statusIp: "IP address",
    statusMac: "Device (MAC)",
    statusBack: "Back to start",
    statusChecking: "Checking your connection...",
    statusFacebook: "Visit WashXpress on Facebook",
    statusNoSession:
      "No session details to show. If pages are loading, your connection is working fine.",

    errNetwork: "Cannot reach the service. Please try again.",
    errGeneric: "Something went wrong. Please try again.",
    errNoHotspot:
      "No hotspot session details found. Please reopen this page through the WiFi connection.",
  },
};

/** Returns the browser's preferred supported locale, defaulting to Thai. */
export function detectLocale() {
  const stored = (() => {
    try {
      return window.localStorage.getItem("wxp-portal-locale");
    } catch {
      return null;
    }
  })();
  if (stored && LOCALES.includes(stored)) return stored;

  const nav = (navigator.language || "").toLowerCase();
  return nav.startsWith("en") ? "en" : "th";
}

export function persistLocale(locale) {
  try {
    window.localStorage.setItem("wxp-portal-locale", locale);
  } catch {
    // Private browsing / storage disabled: fall back to in-memory only.
  }
}

/**
 * Looks up `key` for `locale` and interpolates {placeholders} from `params`.
 */
export function translate(locale, key, params = {}) {
  const dict = messages[locale] || messages.th;
  const raw = dict[key] ?? messages.th[key] ?? key;
  return raw.replace(/\{(\w+)\}/g, (_, name) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : `{${name}}`
  );
}

export default messages;
